import axios from "axios";

export type ApiErrorBody = {
  code?: string;
  message?: string;
};

export type ParsedApiError = {
  status?: number;
  code?: string;
  message?: string;
  isNetworkError: boolean;
};

export const API_ERROR_CODES = {
  invalidRequest: "INVALID_REQUEST",
  internalServerError: "INTERNAL_SERVER_ERROR",
  duplicateEmail: "portalusers.duplicate_email",
  invalidCredentials: "portalusers.invalid_credentials",
  accountNotActive: "portalusers.account_not_active",
  loginRateLimited: "portalusers.login_rate_limited",
  loginLocked: "portalusers.login_locked",
  invalidResetToken: "portalusers.invalid_reset_token",
  malformedResetToken: "portalusers.malformed_reset_token",
  invalidVerificationToken: "portalusers.invalid_verification_token",
  expiredVerificationToken: "portalusers.expired_verification_token",
  malformedVerificationToken: "portalusers.malformed_verification_token",
  alreadyVerified: "portalusers.already_verified",
  adminInvalidResetToken: "adminusers.invalid_reset_token",
  adminMalformedResetToken: "adminusers.malformed_reset_token",
  jwtTokenExpired: "jwt.token_expired",
  jwtTokenInvalid: "jwt.token_invalid",
  jwtAuthenticationRequired: "jwt.authentication_required",
  portalUserUnauthorized: "portalusers.unauthorized",
  accessRequestUnauthorized: "accessrequests.unauthorized",
} as const;

const JWT_AUTH_ERROR_MESSAGES: Record<string, string> = {
  [API_ERROR_CODES.jwtTokenExpired]:
    "Your session has expired. Please log in again.",
  [API_ERROR_CODES.jwtTokenInvalid]: "Invalid authentication token.",
  [API_ERROR_CODES.jwtAuthenticationRequired]: "Authentication required",
};

export function parseApiError(err: unknown): ParsedApiError {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as ApiErrorBody | undefined;
    return {
      status,
      code: data?.code,
      message: data?.message,
      isNetworkError: !err.response,
    };
  }

  const fallback = err as {
    response?: { status?: number; data?: ApiErrorBody };
  };

  return {
    status: fallback.response?.status,
    code: fallback.response?.data?.code,
    message: fallback.response?.data?.message,
    isNetworkError: fallback.response === undefined,
  };
}

export function isJwtAuthErrorCode(code?: string): boolean {
  return (
    code === API_ERROR_CODES.jwtTokenExpired ||
    code === API_ERROR_CODES.jwtTokenInvalid ||
    code === API_ERROR_CODES.jwtAuthenticationRequired
  );
}

export function isProtectedRouteAuthErrorCode(code?: string): boolean {
  return (
    isJwtAuthErrorCode(code) ||
    code === API_ERROR_CODES.portalUserUnauthorized ||
    code === API_ERROR_CODES.accessRequestUnauthorized
  );
}

export function getJwtAuthErrorMessage(
  code?: string,
  fallbackMessage?: string,
): string | null {
  if (!code || !isJwtAuthErrorCode(code)) {
    return null;
  }

  return fallbackMessage ?? JWT_AUTH_ERROR_MESSAGES[code] ?? null;
}

export function getProtectedRouteAuthErrorMessage(
  code?: string,
  fallbackMessage?: string,
): string | null {
  const jwtMessage = getJwtAuthErrorMessage(code, fallbackMessage);
  if (jwtMessage) {
    return jwtMessage;
  }

  if (code === API_ERROR_CODES.portalUserUnauthorized) {
    return fallbackMessage ?? "Authentication required";
  }

  if (code === API_ERROR_CODES.accessRequestUnauthorized) {
    return fallbackMessage ?? "Your session has expired. Please log in again.";
  }

  return null;
}
